import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Loader, Tabs, Alert } from '@itds/elements/index.js';

import Config from '@/config';

import General from './General';
import EmbedCode from './EmbedCode';
import CustomActions from './CutomActions';

import LimitStates from '@brokerage/components/LimitStates';

import { Api } from '@brokerage/utils/Api';
import LimitCarriers from './LimitCarriers';
import LimitProductTypes from './LimitProductTypes';

const Show = ({ detail, token, controlledTabs, controlledTabKey, onTabChange, onParentSave }) => {
  const params = useParams();
  const [ltc_quoter, setLtcQuoter] = useState(detail || {});
  const [loading, setLoading] = useState(!detail);
  const [error_message, setErrorMessage] = useState('');
  const [success_message, setSuccessMessage] = useState('');
  // Set default tab: 'general' for standalone, 'product_defaults' for controlled
  const [activeTab, setActiveTab] = useState(
    controlledTabKey || (controlledTabs ? 'product_defaults' : 'general')
  );

  useEffect(() => {
    if (!detail) loadLtcQuoter();
  }, [detail]);

  useEffect(() => {
    if (controlledTabKey) setActiveTab(controlledTabKey);
  }, [controlledTabKey]);

  const loadLtcQuoter = () => {
    Api.loadLtcQuoter(params.id).then(({ data }) => {
      setLtcQuoter(data);
      setLoading(false);
    });
  };

  const handleSave = input_fields => {
    window.scrollTo(0, 0);
    // If in controlled mode, call parent handleSave
    if (controlledTabs && typeof onParentSave === 'function') {
      onParentSave(input_fields, {
        setErrorMessage,
        setSuccessMessage
      });
      return;
    }
    if (detail) return; // No-op if controlled by Universal Quoter
    Api.updateLtcQuoter(params.id, input_fields)
      .then(() => {
        loadLtcQuoter();
        setErrorMessage('');
        setSuccessMessage('LTC updated successfully');
        setTimeout(() => {
          setSuccessMessage('');
        }, 2000);
      })
      .catch(() => {
        setSuccessMessage('');
        setErrorMessage('Unable to update LTC');
        setTimeout(() => {
          setErrorMessage('');
        }, 2000);
      });
  };

  // Tabs for controlled (Universal Quoter) mode
  const uqTabs = [
    { label: 'Product Defaults', key: 'product_defaults' },
    { label: 'Limit Carriers', key: 'limit_carriers' },
    { label: 'Limit Product Types', key: 'limit_product_types' },
    { label: 'Limit States', key: 'limit_states' },
    { label: 'Custom Actions', key: 'custom_actions' }
  ];
  // Tabs for standalone mode
  const standaloneTabs = [
    { label: 'General', key: 'general' },
    { label: 'Limit Carriers', key: 'limit_carriers' },
    { label: 'Limit Product Types', key: 'limit_product_types' },
    { label: 'Limit States', key: 'limit_states' },
    { label: 'Custom Actions', key: 'custom_actions' },
    { label: 'Preview', key: 'preview' },
    { label: 'Embed Code', key: 'embed_code' }
  ];
  const tabs = controlledTabs ? uqTabs : standaloneTabs;

  // Map tab key to component
  const isControlled = !!controlledTabs;
  const quoterData = isControlled ? detail : ltc_quoter;
  const child_props = {
    ltc_quoter: quoterData,
    handleSave,
    loadLtcQuoter,
    setLtcQuoter,
    setErrorMessage,
    setSuccessMessage
  };
  const tabContent = {
    product_defaults: <General {...child_props} controlledMode={isControlled} />, // Universal Quoter mode
    general: <General {...child_props} controlledMode={isControlled} />, // Standalone mode
    limit_carriers: (
      <LimitCarriers {...child_props} handleSave={handleSave} ltc_quoter={quoterData} />
    ), // use latest data
    limit_product_types: (
      <LimitProductTypes {...child_props} handleSave={handleSave} quoter={quoterData} />
    ),
    limit_products: (
      <LimitProductTypes {...child_props} handleSave={handleSave} quoter={quoterData} />
    ),
    limit_states: (
      <LimitStates
        defaultState={quoterData.default_state}
        handleSave={handleSave}
        selectedStates={quoterData.states}
      />
    ),
    custom_actions: <CustomActions {...child_props} />,
    preview: null, // No-op in render
    embed_code: !controlledTabs ? <EmbedCode {...child_props} /> : null
  };

  // Handle tab click
  const handleTabClick = tabKey => {
    if (!controlledTabs && tabKey === 'preview') {
      window.open(`${Config.LTC_URL}?token=${token || ltc_quoter.id}`);
      setActiveTab('general');
      if (onTabChange) onTabChange('general');
      return;
    }
    setActiveTab(tabKey);
    if (onTabChange) onTabChange(tabKey);
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {error_message ? <Alert type='error'>{error_message}</Alert> : null}
      {success_message ? <Alert type='success'>{success_message}</Alert> : null}
      {!controlledTabs ? (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Tabs
            tabs={tabs.map(tab => ({
              label: tab.label,
              to: tab.key,
              isActive: activeTab === tab.key,
              onClick: () => handleTabClick(tab.key)
            }))}
            vertical={true}
          />
          <div style={{ flex: 1, paddingLeft: 40 }}>{tabContent[activeTab]}</div>
        </div>
      ) : (
        <>{tabContent[activeTab]}</>
      )}
    </>
  );
};

Show.propTypes = {
  controlledTabKey: PropTypes.string,
  controlledTabs: PropTypes.array,
  detail: PropTypes.object,
  name: PropTypes.string,
  onParentSave: PropTypes.func,
  onTabChange: PropTypes.func,
  token: PropTypes.string
};

Show.General = General;
export default Show;
